import { useState, type JSX } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { BulkImportWizard } from '@/components/bulk-import/BulkImportWizard';
import { useMe } from '@/hooks/useMe';
import { TWO_STEP_SHORT } from '@/lib/outreach-copy';
import type { Me } from '@/api/types';

type OutreacherMe = Extract<Me, { role: 'outreacher' }>;

/**
 * Bulk import: upload a CSV or Excel list of creators, then mark them all
 * messaged or log their replies in one go. Only for outreachers an admin has
 * allowed (per team on Team rules, or per outreacher on their profile).
 */
export function BulkImportPage(): JSX.Element {
  const me = useMe();
  // The server can still refuse (rule changed since /me loaded): flip to the locked state.
  const [refused, setRefused] = useState(false);

  const header = (
    <PageHeader
      title="Bulk import"
      description={
        <>
          Upload a CSV or Excel list of creators, then mark them all as messaged or log their replies in one go.{' '}
          {TWO_STEP_SHORT}
        </>
      }
    />
  );

  if (me.isLoading) {
    return (
      <div className="space-y-6">
        {header}
        <p className="flex items-center text-sm text-muted-foreground" aria-live="polite">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </p>
      </div>
    );
  }

  const outreacher: OutreacherMe | null = me.data?.role === 'outreacher' ? me.data : null;
  if (!outreacher || outreacher.bulkImportEnabled !== true || refused) {
    return (
      <div className="space-y-6">
        {header}
        <div className="panel rounded-xl">
          <EmptyState
            icon={<Lock className="h-6 w-6" />}
            title="Ask an admin to turn on bulk import"
            hint="Bulk import is switched on for some teams and outreachers only. Until then, use Log reply one creator at a time."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      <BulkImportWizard
        allowedChannels={outreacher.team?.allowedChannels ?? []}
        allowedCategories={outreacher.team?.allowedCategories ?? []}
        screenshotRequired={outreacher.teamSettings.screenshotRequired}
        onNotAllowed={() => setRefused(true)}
      />
    </div>
  );
}
